/**
 * Random number source for wheel spins.
 *
 * Uses crypto.getRandomValues where available so the fairness claims on the
 * landing pages are literally true, and falls back to Math.random only where
 * the Web Crypto API is missing (server-side rendering, very old browsers).
 * Every browser this site supports has it.
 */

/**
 * A uniform float in [0, 1) — never 1.
 *
 * Note the divisor: 2**32, not 0xFFFFFFFF. Dividing by 0xFFFFFFFF returns
 * exactly 1.0 for the largest draw, which would push `randomFloat() * total`
 * past the last cumulative bound in weightedIndex and fall through to its
 * guard clause instead of selecting normally. One draw in four billion, but
 * the kind of thing that is impossible to reproduce once reported.
 */
const UINT32_RANGE = 4294967296;
const UINT53_RANGE = 9007199254740992;

/** A uniformly distributed 53-bit integer in [0, 2**53). */
function cryptoUint53(): number | null {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(2);
    crypto.getRandomValues(buf);
    // A JavaScript number represents every integer through 2**53 exactly.
    return (buf[0] & 0x1fffff) * UINT32_RANGE + buf[1];
  }
  return null;
}

/** A uniform float in [0, 1) -- never 1. */
export function randomFloat(): number {
  const value = cryptoUint53();
  if (value !== null) return value / UINT53_RANGE;
  return Math.random();
}

/**
 * A uniform integer in [0, n). Returns 0 for invalid or non-positive bounds.
 * Rejection sampling avoids modulo/scaling bias when 2**53 is not divisible
 * by the requested range.
 */
export function randomInt(n: number): number {
  const bound = Math.floor(n);
  if (!Number.isSafeInteger(bound) || bound <= 0) return 0;

  const cryptoValue = cryptoUint53();
  if (cryptoValue === null) return Math.floor(Math.random() * bound);

  const limit = Math.floor(UINT53_RANGE / bound) * bound;
  let value = cryptoValue;
  while (value >= limit) value = cryptoUint53() as number;
  return value % bound;
}

/**
 * Pick an index with probability proportional to `weights[i]`.
 *
 * Non-positive and non-finite weights are treated as 0 — they can never be
 * selected. Returns -1 when no entry has any weight at all, so callers must
 * handle "nothing selectable" rather than silently spinning to index 0.
 */
export function weightedIndex(weights: number[]): number {
  // Application weights are stored to two decimal places. Integer hundredths
  // let the same rejection-sampled path make an exact weighted selection.
  const units = weights.map(w => Number.isFinite(w) && w > 0 ? Math.round(w * 100) : 0);
  const total = units.reduce((sum, value) => sum + value, 0);
  if (!Number.isSafeInteger(total) || total <= 0) return -1;

  const target = randomInt(total);
  let cumulative = 0;
  for (let i = 0; i < units.length; i++) {
    if (units[i] === 0) continue;
    cumulative += units[i];
    if (target < cumulative) return i;
  }

  // Defensive guard; exact integer arithmetic should make this unreachable.
  for (let i = units.length - 1; i >= 0; i--) if (units[i] > 0) return i;
  return -1;
}
